import { Plus, Camera, CaretDown, UserPlus, FileText, UploadSimple, Sparkle, Stethoscope } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { RowanTerm, ScreenTerms, askRowan } from "@/components/RowanTerm";
import { SECTIONS, MAIN_ACTIONS, MONEY_ACTIONS } from "@/lib/workspaceNav";

const ACTION_ICONS = { home: Camera, money: Plus, customers: UserPlus, business: Plus, reports: FileText, vault: UploadSimple };

function MainActionButton({ section, view, onAction, menu }) {
  const meta = (section === "money" && MONEY_ACTIONS[view]) || MAIN_ACTIONS[section];
  const Icon = section === "money" && MONEY_ACTIONS[view] ? (view === "scan" || view === "receipts" ? Camera : Plus) : (ACTION_ICONS[section] || Plus);
  const cls = "bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold shrink-0";
  if (!menu?.length) {
    return (
      <button onClick={() => onAction(section)} data-testid={meta.testid} className={cls}>
        <Icon size={16} weight="bold" /> {meta.label}
      </button>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={meta.testid} className={cls}>
          <Icon size={16} weight="bold" /> {meta.label} <CaretDown size={14} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl min-w-[220px]" data-testid={`${meta.testid}-menu`}>
        {menu.map((m) => (
          <DropdownMenuItem key={m.key} onSelect={() => onAction(section, m.key)} data-testid={`${meta.testid}-${m.key}`} className="text-sm py-2.5 cursor-pointer">
            {m.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SectionHeader({ section, views, active, onChange, onAction, menu, secondary }) {
  const meta = SECTIONS.find((s) => s.key === section);
  const current = views.find((v) => v.key === active) || views[0];
  return (
    <div className="bk-card p-5 sm:p-6 mb-6" data-testid={`section-header-${section}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-bold tracking-tight" style={{ color: "var(--bk-text)" }} data-testid="section-title">{meta?.label}</h2>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="view-desc">
            {current?.term ? <RowanTerm term={current.term}>{current.desc}</RowanTerm> : current?.desc}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
          {secondary && (
            <button onClick={secondary.onClick} data-testid={secondary.testid} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors hover:bg-[#F6EEDF] max-w-full" style={{ borderColor: "var(--bk-accent)", color: "var(--bk-accent)", background: "#fff" }}>
              <Stethoscope size={16} weight="bold" /> {secondary.label}
            </button>
          )}
          <MainActionButton section={section} view={active} onAction={onAction} menu={menu} />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <ScreenTerms terms={current?.terms || []} />
        <button
          type="button"
          onClick={() => askRowan(`I'm on the ${meta?.label} → ${current?.label} screen. In plain language: what am I looking at, what do the numbers mean, and what should I do next?`, `${current?.label} screen`)}
          data-testid="ask-rowan-screen"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors hover:bg-[#F6EEDF]"
          style={{ color: "var(--bk-accent)", border: "1px solid var(--bk-border)" }}
        >
          <Sparkle size={13} weight="fill" color="var(--bk-gold)" /> Ask Rowan about this screen
        </button>
      </div>
      {views.length > 1 && (
        <div className="flex flex-wrap gap-2 mt-4" role="tablist" data-testid={`subtabs-${section}`}>
          {views.map((v) => {
            const isActive = v.key === active;
            return (
              <button
                key={v.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange(v.key)}
                data-testid={`tab-${v.key}`}
                className="px-4 py-2 rounded-full text-sm font-semibold transition-colors"
                style={isActive ? { background: "var(--bk-accent)", color: "#fff" } : { border: "1px solid var(--bk-border)", color: "var(--bk-text-2)", background: "#fff" }}
              >
                {v.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
